/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { JSX } from "react"
import { useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { UserAppTypeViewModel } from "../../features/user/user-apptype-viewmodel"
import { UserDetailViewModel } from "../../features/user/user-detail-view-model"
import { UserTotpViewModel } from "../../features/user/user-totp-viewmodel"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { useViewModel, useViewModelWithArgs } from "../app/app-hooks"

import { AssignedApplicationsCard } from "./assigned-applications-card"
import { DangerZoneCard } from "./danger-zone-modal"
import { UserDetailCard } from "./user-detail-card"
import { UserEditAppsModal } from "./user-edit-apps-modal"

export const UserDetailView = observer((): JSX.Element => {
    const { id } = useParams<{ id: string }>()
    const userId = id ? Number(id) : NaN
    const [searchParams] = useSearchParams()
    const orgIdParam = searchParams.get("orgId")
    const orgId = orgIdParam ? Number(orgIdParam) : -1

    const userVM = useViewModelWithArgs(UserDetailViewModel, userId)
    const userAppTypeVM = useViewModel(UserAppTypeViewModel)
    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const loggerVM = useViewModel(LoggerViewModel)
    const totpVM = useViewModel(UserTotpViewModel)

    const [showDangerZoneModal, setShowDangerZoneModal] = useState(false)
    const [showEditAppsModal, setShowEditAppsModal] = useState(false)

    const navigate = useNavigate()
    const isExternal = userVM.userDetail?.organizationId !== orgId

    if (!userVM.userDetail) {
        return (
            <div className="page-container mt-6">
                <div className="alert alert-warning">User not found.</div>
            </div>
        )
    }

    const handleRemoveExternalUser = async () => {
        const result = await userAppTypeVM.removeExternalUser(userId)
        if (result.success) {
            loggerVM.logOk("External User successfully removed from your organization.")
            navigate(AppRoutes.AllUsers)
        } else {
            loggerVM.logErrorMessage("Failed to remove user.")
            // Removal is done automatically on BE when all apps of the external user are removed
            // So after failure, get the latest user's apps
            void userAppTypeVM.getUserAppTypes(userId)
        }
    }

    const handleDeleteUser = async () => {
        const result = await userVM.deleteOrganizationUser()

        if (result.success) {
            loggerVM.logOk("User successfully deleted.")
            navigate(AppRoutes.AllUsers)
        } else {
            loggerVM.logErrorMessage("Failed to Delete User.")
        }
    }

    const handleResetPassword = async () => {
        const result = await userVM.resetUserPassword()

        if (result.success) {
            loggerVM.logOk("Password successfully reset.")
        } else {
            loggerVM.logErrorMessage("Failed to reset password.")
        }
    }

    const handleResetTOTP = async () => {
        await totpVM.resetTotp(userId)
    }

    const handleEditAppsModal = async () => {
        if (!orgId) return

        setShowEditAppsModal(true)
        await appTypeVM.getAppTypes(orgId) // fetch all app types of the current organization to display them in modal
    }

    const handleSavingAppTypes = async (selectedAppTypeIds: number[]) => {
        if (!orgId) return
        const shouldConfirm = userAppTypeVM.shouldConfirmUnassignment(isExternal, selectedAppTypeIds)

        if (shouldConfirm) {
            const confirmed = window.confirm(
                "This is an external user.\nRemoving all assigned applications will unassign them from your organization.\nDo you want to continue?"
            )
            if (!confirmed) return
        }

        const result = await userAppTypeVM.updateUserAppTypes(userId, orgId, selectedAppTypeIds)

        if (!result.success) {
            loggerVM.logErrorMessage(
                result.error instanceof Error ? result.error.message : "Failed to update user apps."
            )
            void userAppTypeVM.getUserAppTypes(userId) // update the app types after partial or all action fail
        }

        if (shouldConfirm) {
            loggerVM.logOk("User has been unassigned from your organization.")
            navigate(AppRoutes.AllUsers)
            return
        }

        // Finally, update all user app types
        void userAppTypeVM.getUserAppTypes(userId)
    }

    return (
        <div className="w-content col-xxl-6 col-xl-8 col-lg-10 col-md-12">
            <div className="col-sm-12">
                {/* Detail card */}
                <UserDetailCard userId={userId} orgId={orgId} onShowDangerZone={() => setShowDangerZoneModal(true)} />
            </div>

            {/* Assigned Applications card */}
            <AssignedApplicationsCard userId={userId} orgId={orgId} onEditClick={handleEditAppsModal} />

            {/* Modal Admin Actions */}
            {showDangerZoneModal && (
                <DangerZoneCard
                    external={isExternal}
                    onRemoveExternalClick={handleRemoveExternalUser}
                    onDeleteUserClick={handleDeleteUser}
                    onResetPasswordClick={handleResetPassword}
                    onResetTOTPClick={handleResetTOTP}
                    onClose={() => setShowDangerZoneModal(false)}
                />
            )}

            {/* Modal Edit Apps */}
            {showEditAppsModal && (
                <UserEditAppsModal
                    userId={userId}
                    orgId={orgId}
                    onClose={() => setShowEditAppsModal(false)}
                    onSave={(selectedAppTypeIds) => {
                        setShowEditAppsModal(false)
                        void handleSavingAppTypes(selectedAppTypeIds)
                    }}
                />
            )}
        </div>
    )
})
